import { Box, Divider, Stack, Typography } from '@mui/material';

import { Creator } from '@/models/creator';

interface Step3Props {
  summary: Creator.BulkValidationSummary | null;
}

interface SummarySectionConfig {
  items: Creator.SummaryItem[];
  getTitle: (n: number) => string;
  description: (n: number) => string;
}

const getSummarySections = (
  summary: Creator.BulkValidationSummary
): SummarySectionConfig[] => [
  {
    items: summary.duplicated_on_same_creator,
    getTitle: (n) => `You have ${n} account${n !== 1 ? 's' : ''} duplicated:`,
    description: (n) =>
      `These account${
        n !== 1 ? 's' : ''
      } are already in your workspace under the same Creator and will not be imported.`
  },
  {
    items: summary.duplicated_on_different_creators,
    getTitle: (n) =>
      `You have ${n} account${
        n !== 1 ? 's' : ''
      } that are already in your workspace under a different Creator.`,
    description: (n) =>
      `These account${
        n !== 1 ? 's' : ''
      } will not be added to avoid duplicate content.`
  },
  {
    items: summary.new_accounts_for_existing_creators,
    getTitle: (n) =>
      `You have ${n} new account${n !== 1 ? 's' : ''} with Creator${
        n !== 1 ? 's' : ''
      } that are already in your workspace.`,
    description: (n) =>
      `These new account${
        n !== 1 ? 's' : ''
      } will be added to existing Creator${n !== 1 ? 's' : ''}.`
  }
];

const Step3 = ({ summary }: Step3Props) => (
  <Stack spacing={3} p={2}>
    <Box border="1px solid #919191" p={2}>
      <Typography variant="h6" mb={2}>
        Import results
      </Typography>
      {summary && (
        <Stack divider={<Divider />} spacing={2}>
          <Typography variant="body2">
            {`${summary.totalCreators} creator${
              summary.totalCreators !== 1 ? 's' : ''
            }, with ${summary.totalAccounts} account${
              summary.totalAccounts !== 1 ? 's' : ''
            }.`}
          </Typography>
          {getSummarySections(summary)
            .filter(({ items }) => items?.length > 0)
            .map(({ items, getTitle, description }, index) => (
              <Box key={index}>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  justifyContent="space-between"
                  spacing={1}
                >
                  <Typography variant="body2" flex={1}>
                    {getTitle(items.length)}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ md: { flex: 1 } }}
                  >
                    {description(items.length)}
                  </Typography>
                </Stack>

                <Box component="ul" pl={2}>
                  {items.map((item, i) => (
                    <Box component="li" key={i}>
                      <Typography variant="body2">
                        {item.creator_name} | {item.account}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            ))}
        </Stack>
      )}
    </Box>
  </Stack>
);

export default Step3;
